import { useTranslation } from 'react-i18next';

import { Icon } from '~/chords';

const NEWS_SKELETON_ROWS = [88, 62, 62, 48, 74];

// Staggered build timing (seconds). The zoom waits until the build settles.
const BUILD = {
  navbar: 0.1,
  search: 0.3,
  pill: 0.5,
  help: 0.68,
  welcomeGrey: 0.9,
  welcomeLime: 1.08,
  divider: 1.26,
  newsStart: 1.44,
  newsStep: 0.15,
};
const ZOOM_DELAY = 2.5;
const ZOOM_DURATION = 2.2;
const GLOW_DELAY = ZOOM_DELAY + ZOOM_DURATION - 0.7;

const fadeIn = (delay: number) => `miqFadeIn 0.45s ease-out ${delay}s both`;

export function LocationIllustration() {
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'musicIqIntroModal.locationIllustration',
  });

  return (
    <div
      style={{
        userSelect: 'none',
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 10',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        backgroundColor: '#131313',
      }}
    >
      <style>{`
        @keyframes miqFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes miqNavZoom {
          from { transform: scale(1); }
          to   { transform: scale(1.6); }
        }
        @keyframes miqNavGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(237,255,67,0); }
          50%      { box-shadow: 0 0 0 3px rgba(237,255,67,0.5), 0 0 16px 3px rgba(237,255,67,0.3); }
        }
      `}</style>

      {/* Zoom stage — scales up around the Music IQ pill */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transformOrigin: '100% 0%',
          animation: `miqNavZoom ${ZOOM_DURATION}s cubic-bezier(0.4, 0, 0.2, 1) ${ZOOM_DELAY}s forwards`,
          backgroundColor: '#131313',
        }}
      >
        {/* Masthead — subtle navy tint on the left plus a faint purple diagonal
            streak near the top, confined to the upper band (matches Figma). The
            news body below stays plain #131313. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '52%',
            background:
              'linear-gradient(115deg, transparent 33%, rgba(110,64,165,0.28) 41%, rgba(96,56,150,0.20) 48%, transparent 56%), linear-gradient(100deg, rgba(22,40,84,0.5) 0%, rgba(19,19,19,0) 42%)',
            // Fade the masthead out toward the bottom (no hard break).
            WebkitMaskImage:
              'linear-gradient(to bottom, black 45%, transparent 100%)',
            maskImage:
              'linear-gradient(to bottom, black 45%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Top nav bar */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '12%',
            minHeight: '34px',
            padding: '0 14px',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            backgroundColor: '#090909',
            animation: fadeIn(BUILD.navbar),
          }}
        >
          {/* Search bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flex: '0 1 55%',
              height: '58%',
              padding: '0 8px',
              borderRadius: '4px',
              backgroundColor: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(240,240,240,0.6)',
              fontSize: '8px',
              fontStyle: 'italic',
              overflow: 'hidden',
              animation: fadeIn(BUILD.search),
            }}
          >
            <Icon
              type="search"
              variant="light"
              size="sm"
              aria-label="search-icon"
              aria-hidden
            />
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('searchPlaceholder')}
            </span>
          </div>

          {/* Right cluster — Music IQ pill + help button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 8px',
                borderRadius: '999px',
                border: '1px solid transparent',
                // Gradient stroke (lime → blue) matching the Figma pill; the dark
                // fill sits on padding-box, the gradient on border-box.
                background:
                  'linear-gradient(180deg, #282828, #191919) padding-box, linear-gradient(90deg, #edff43, #1f65df) border-box',
                animation: `${fadeIn(BUILD.pill)}, miqNavGlow 1.8s ease-in-out ${GLOW_DELAY}s infinite`,
              }}
            >
              <span
                style={{
                  fontSize: '8px',
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '0.2px',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('button')}
              </span>
              <img
                src="/images/music-iq/music-iq.svg"
                alt=""
                style={{ width: '11px', height: '11px', display: 'block' }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.85)',
                color: '#fff',
                fontSize: '8px',
                animation: fadeIn(BUILD.help),
              }}
            >
              ?
            </div>
          </div>
        </div>

        {/* Body — welcome + news skeletons */}
        <div
          style={{
            position: 'relative',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px',
          }}
        >
          <div
            style={{
              width: '22%',
              height: '9px',
              borderRadius: '6px',
              backgroundColor: 'rgba(217,217,217,0.25)',
              animation: fadeIn(BUILD.welcomeGrey),
            }}
          />
          <div
            style={{
              width: '32%',
              height: '17px',
              borderRadius: '6px',
              backgroundColor: 'rgba(237,255,67,0.25)',
              animation: fadeIn(BUILD.welcomeLime),
            }}
          />
          <div
            style={{
              height: '1px',
              backgroundColor: 'rgba(255,255,255,0.10)',
              margin: '5px 0',
              animation: fadeIn(BUILD.divider),
            }}
          />
          {NEWS_SKELETON_ROWS.map((width, i) => (
            <div
              key={i}
              style={{
                width: `${width}%`,
                height: '7px',
                borderRadius: '6px',
                backgroundColor: 'rgba(217,217,217,0.18)',
                animation: fadeIn(BUILD.newsStart + i * BUILD.newsStep),
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

LocationIllustration.displayName = 'LocationIllustration';
